import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, uploadFile } from "@/lib/api";
import {
  Loader2, Camera, Upload, X, Mic, Square, CheckCircle2, AlertTriangle,
  MessageSquare, Save, Lock, Building2, Sun, HelpCircle,
} from "lucide-react";

const QQOCP_FIELDS = [
  { key: "qui", label: "Qui ?", placeholder: "Qui réalise cette tâche ? (toi, un collègue, le responsable…)" },
  { key: "quoi", label: "Quoi ?", placeholder: "En quoi consiste la tâche exactement ?" },
  { key: "ou", label: "Où ?", placeholder: "Dans quel rayon, quel lieu de l'entreprise ?" },
  { key: "quand", label: "Quand ?", placeholder: "À quel moment de la journée, quelle fréquence ?" },
  { key: "comment", label: "Comment ?", placeholder: "Avec quels outils, quelle méthode, quelles étapes ?" },
  { key: "pourquoi", label: "Pourquoi ?", placeholder: "Quel est l'objectif, à quoi ça sert ?" },
];
const EMPTY_QQOCP = { qui: "", quoi: "", ou: "", quand: "", comment: "", pourquoi: "" };

function StatusBadge({ status }) {
  if (status === "validated") return <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full" data-testid="portfolio-status-badge"><CheckCircle2 className="h-3.5 w-3.5" /> Validé</span>;
  if (status === "to_revise") return <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-amber-700 bg-amber-100 px-3 py-1 rounded-full" data-testid="portfolio-status-badge"><AlertTriangle className="h-3.5 w-3.5" /> À revoir</span>;
  return <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-slate-500 bg-slate-100 px-3 py-1 rounded-full" data-testid="portfolio-status-badge">En cours</span>;
}

function PhotosField({ photos, onAdd, onRemove, disabled }) {
  const inputRef = useRef(null);
  return (
    <div>
      <p className="text-sm font-bold text-slate-700 mb-2 inline-flex items-center gap-1.5"><Camera className="h-4 w-4 text-orange-500" /> Photos</p>
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
          {photos.map((p, i) => (
            <div key={i} className="relative group">
              <img src={p.id ? fileUrl(p.id) : p.previewUrl} alt={p.name} className="h-20 w-full object-cover rounded-xl border border-slate-200" />
              {!disabled && (
                <button type="button" onClick={() => onRemove(i)} data-testid={`portfolio-photo-remove-${i}`}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow"><X className="h-3 w-3" /></button>
              )}
            </div>
          ))}
        </div>
      )}
      {!disabled && (
        <>
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="portfolio-photo-input"
            onChange={(e) => { if (e.target.files.length) onAdd(e.target.files); e.target.value = ""; }} />
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="portfolio-photo-add-btn"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 hover:border-orange-300 text-slate-600 hover:text-orange-600 px-4 py-2 text-xs font-bold transition-colors">
            <Upload className="h-3.5 w-3.5" /> Ajouter des photos
          </button>
        </>
      )}
    </div>
  );
}

function AudioField({ audio, audioBlob, audioUrl, onRecordStart, onRecordStop, recording, onPick, onClear, disabled }) {
  const inputRef = useRef(null);
  const currentUrl = audioUrl || (audio ? fileUrl(audio.id) : null);
  return (
    <div>
      <p className="text-sm font-bold text-slate-700 mb-2 inline-flex items-center gap-1.5"><Mic className="h-4 w-4 text-orange-500" /> Présentation orale (audio)</p>
      {currentUrl && <audio src={currentUrl} controls className="w-full h-10 mb-2" data-testid="portfolio-audio-preview" />}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-2">
          {!recording ? (
            <button type="button" onClick={onRecordStart} data-testid="portfolio-audio-record-btn"
              className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 hover:border-orange-300 text-slate-600 hover:text-orange-600 px-4 py-2 text-xs font-bold transition-colors">
              <Mic className="h-3.5 w-3.5" /> Enregistrer au micro
            </button>
          ) : (
            <button type="button" onClick={onRecordStop} data-testid="portfolio-audio-stop-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-500 text-white px-4 py-2 text-xs font-bold animate-pulse">
              <Square className="h-3.5 w-3.5" /> Arrêter l'enregistrement
            </button>
          )}
          <input ref={inputRef} type="file" accept="audio/*" className="hidden" data-testid="portfolio-audio-input"
            onChange={(e) => e.target.files[0] && onPick(e.target.files[0])} />
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="portfolio-audio-upload-btn"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 hover:border-orange-300 text-slate-600 hover:text-orange-600 px-4 py-2 text-xs font-bold transition-colors">
            <Upload className="h-3.5 w-3.5" /> Choisir un fichier audio
          </button>
          {(audioBlob || audio) && (
            <button type="button" onClick={onClear} data-testid="portfolio-audio-clear-btn" className="text-xs font-bold text-red-500 hover:text-red-600">Retirer</button>
          )}
        </div>
      )}
    </div>
  );
}

function PfmpPortfolioForm({ pfmp }) {
  const [portfolio, setPortfolio] = useState(undefined);
  const [company, setCompany] = useState("");
  const [typicalDay, setTypicalDay] = useState("");
  const [qqocp, setQqocp] = useState(EMPTY_QQOCP);
  const [photos, setPhotos] = useState([]);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioCleared, setAudioCleared] = useState(false);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const load = () => {
    api.get(`/career/pfmp-portfolio/${pfmp.id}`).then(({ data }) => {
      setPortfolio(data);
      setCompany(data?.company_description || "");
      setTypicalDay(data?.typical_day || "");
      setQqocp({ ...EMPTY_QQOCP, ...(data?.qqocp || {}) });
      setPhotos(data?.photos || []);
      setAudioCleared(false);
      setAudioBlob(null);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }).catch(() => setPortfolio(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [pfmp.id]);

  const locked = portfolio?.validation_status === "validated";

  const addPhotos = (fileList) => {
    const arr = Array.from(fileList).map((f) => ({ file: f, name: f.name, previewUrl: URL.createObjectURL(f) }));
    setPhotos((prev) => [...prev, ...arr]);
  };
  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, j) => j !== i));

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setAudioCleared(false);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Micro inaccessible. Vérifie les autorisations du navigateur.");
    }
  };
  const stopRecording = () => { recorderRef.current?.stop(); setRecording(false); };
  const pickAudioFile = (file) => {
    setAudioBlob(file);
    setAudioUrl(URL.createObjectURL(file));
    setAudioCleared(false);
  };
  const clearAudio = () => { setAudioBlob(null); setAudioUrl(null); setAudioCleared(true); };

  const save = async () => {
    setBusy(true);
    try {
      const photoIds = [];
      for (const p of photos) {
        if (p.id) photoIds.push(p.id);
        else {
          const up = await uploadFile(p.file);
          photoIds.push(up.id);
        }
      }
      let audioId = portfolio?.audio?.id || null;
      if (audioBlob) {
        const file = audioBlob instanceof File ? audioBlob : new File([audioBlob], `presentation-${pfmp.id}.webm`, { type: audioBlob.type || "audio/webm" });
        const up = await uploadFile(file);
        audioId = up.id;
      } else if (audioCleared) {
        audioId = null;
      }
      const { data } = await api.put(`/career/pfmp-portfolio/${pfmp.id}`, {
        company_description: company, typical_day: typicalDay, qqocp, photo_ids: photoIds, audio_id: audioId,
      });
      setPortfolio(data);
      setPhotos(data.photos || []);
      setAudioBlob(null); setAudioCleared(false);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
      toast.success("Portfolio PFMP enregistré ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement.");
    } finally { setBusy(false); }
  };

  if (portfolio === undefined) return <div className="rounded-3xl border border-slate-100 bg-white p-8 flex justify-center"><Loader2 className="h-6 w-6 text-orange-400 animate-spin" /></div>;

  return (
    <div className="rounded-3xl border border-slate-100 bg-white shadow-sm p-5 sm:p-6" data-testid={`pfmp-portfolio-form-${pfmp.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div>
          <p className="font-display text-lg font-semibold text-slate-900">{pfmp.title}</p>
          <p className="text-sm text-slate-500 font-medium">{pfmp.period}</p>
        </div>
        <StatusBadge status={portfolio?.validation_status || "pending"} />
      </div>

      {locked && (
        <div className="flex items-center gap-2 mt-3 bg-emerald-50 text-emerald-700 rounded-2xl px-4 py-3" data-testid="portfolio-locked-notice">
          <Lock className="h-5 w-5 shrink-0" />
          <p className="text-sm font-bold">Ce portfolio a été validé par ton professeur, il n'est plus modifiable.</p>
        </div>
      )}
      {portfolio?.teacher_comment && (
        <div className="mt-3 rounded-xl bg-orange-50 border border-orange-100 p-3" data-testid="portfolio-teacher-comment">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-orange-700 mb-1 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Commentaire du professeur</p>
          <p className="text-sm text-slate-700">{portfolio.teacher_comment}</p>
        </div>
      )}

      <fieldset disabled={locked || busy} className="space-y-5 mt-5">
        <div>
          <label className="text-sm font-bold text-slate-700 mb-1.5 inline-flex items-center gap-1.5"><Building2 className="h-4 w-4 text-orange-500" /> Présentation de l'entreprise</label>
          <textarea value={company} onChange={(e) => setCompany(e.target.value)} rows={3} data-testid="portfolio-company-input"
            placeholder="Nom de l'entreprise, son activité, sa taille, son organisation…"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:bg-slate-50 disabled:text-slate-500" />
        </div>

        <div>
          <label className="text-sm font-bold text-slate-700 mb-1.5 inline-flex items-center gap-1.5"><Sun className="h-4 w-4 text-orange-500" /> Une journée type</label>
          <textarea value={typicalDay} onChange={(e) => setTypicalDay(e.target.value)} rows={3} data-testid="portfolio-typicalday-input"
            placeholder="Raconte le déroulement d'une journée type dans l'entreprise…"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:bg-slate-50 disabled:text-slate-500" />
        </div>

        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 inline-flex items-center gap-1.5"><HelpCircle className="h-4 w-4 text-orange-500" /> Une tâche professionnelle — méthode QQOQCP</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {QQOCP_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-xs font-bold text-slate-500 mb-1 block">{f.label}</label>
                <textarea value={qqocp[f.key]} onChange={(e) => setQqocp((prev) => ({ ...prev, [f.key]: e.target.value }))} rows={2}
                  placeholder={f.placeholder} data-testid={`portfolio-qqocp-${f.key}`}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:bg-slate-50 disabled:text-slate-500" />
              </div>
            ))}
          </div>
        </div>

        <PhotosField photos={photos} onAdd={addPhotos} onRemove={removePhoto} disabled={locked} />
        <AudioField
          audio={portfolio?.audio} audioBlob={audioBlob} audioUrl={audioUrl} recording={recording}
          onRecordStart={startRecording} onRecordStop={stopRecording} onPick={pickAudioFile} onClear={clearAudio} disabled={locked}
        />
      </fieldset>

      {!locked && (
        <button onClick={save} disabled={busy} data-testid={`portfolio-save-${pfmp.id}`}
          className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-full px-6 py-2.5 font-bold text-sm transition-colors">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer mon portfolio
        </button>
      )}
    </div>
  );
}

export default function PfmpPortfolioPage() {
  const [pfmps, setPfmps] = useState(null);

  useEffect(() => {
    api.get("/pfmp/list").then(({ data }) => setPfmps(data)).catch(() => setPfmps([]));
  }, []);

  if (pfmps === null) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 text-orange-500 animate-spin" /></div>;

  return (
    <div className="space-y-8" data-testid="pfmp-portfolio-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-3">
          <Camera className="h-8 w-8 text-orange-500" /> Portfolio PFMP
        </h1>
        <p className="text-slate-500 font-medium mt-1">Pour chaque PFMP, présente l'entreprise, raconte une journée type et détaille une tâche professionnelle avec la méthode QQOQCP. Ajoute des photos et une présentation orale.</p>
      </div>

      <div className="space-y-5">
        {pfmps.map((p) => <PfmpPortfolioForm key={p.id} pfmp={p} />)}
        {pfmps.length === 0 && <p className="text-sm text-slate-400 font-medium">Aucune PFMP programmée pour ton année.</p>}
      </div>
    </div>
  );
}
